import { User } from "@/types";

// Глобальный объект Telegram WebApp
declare global {
  interface Window {
    Telegram: {
      WebApp: TelegramWebApp;
    };
  }
}

// Интерфейс для данных пользователя Telegram
export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

// Расширенный интерфейс для параметров темы Telegram
export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

// Интерфейс для Telegram WebApp
export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    query_id?: string;
    user?: TelegramUser;
    auth_date: number;
    hash: string;
  };
  colorScheme: "light" | "dark";
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  onEvent(eventType: string, eventHandler: () => void): void;
  offEvent(eventType: string, eventHandler: () => void): void;
  sendData(data: string): void;
  openLink(url: string): void;
  openTelegramLink(url: string): void;
  showPopup(params: object, callback?: () => void): void;
  showAlert(message: string, callback?: () => void): void;
  showConfirm(message: string, callback?: () => void): void;
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    setText(text: string): void;
    onClick(callback: () => void): void;
    offClick(callback: () => void): void;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    showProgress(leaveActive: boolean): void;
    hideProgress(): void;
  };
  BackButton: {
    isVisible: boolean;
    onClick(callback: () => void): void;
    offClick(callback: () => void): void;
    show(): void;
    hide(): void;
  };
  HapticFeedback: {
    impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
    selectionChanged(): void;
  };
}

// Проверка доступности API Telegram WebApp (независимо от наличия initData)
export const isTelegramApiAvailable = (): boolean =>
  Boolean(window.Telegram && window.Telegram.WebApp);

// Проверка, запущено ли приложение в Telegram и содержит ли initData
export const isTelegramWebApp = (): boolean => {
  const apiAvailable = isTelegramApiAvailable();
  const initData = window.Telegram?.WebApp?.initData;
  const initDataLength = initData?.length ?? 0;
  const hasInitData = Boolean(initDataLength);

  const result = apiAvailable && hasInitData;

  console.log("isTelegramWebApp check:", {
    windowTelegram: Boolean(window.Telegram),
    windowTelegramWebApp: Boolean(window.Telegram?.WebApp),
    initDataExists: Boolean(initData),
    initDataLength,
    result,
  });

  if (result) {
    console.log("Telegram WebApp initData:", initData);
    console.log("Telegram WebApp initDataUnsafe:", window.Telegram?.WebApp?.initDataUnsafe);
  }

  return result;
};

// Проверка наличия initData без учёта доступности API (удобно для доп. проверок)
export const hasTelegramInitData = (): boolean =>
  Boolean(window.Telegram?.WebApp?.initData?.length);

// Получение данных пользователя Telegram
export const getTelegramUser = (): TelegramUser | null => {
  if (isTelegramWebApp() && window.Telegram.WebApp.initDataUnsafe.user) {
    return window.Telegram.WebApp.initDataUnsafe.user;
  }
  return null;
};

// Получение magic link для авторизации через Telegram
export const getTelegramMagicLink = async (): Promise<string> => {
  if (!isTelegramWebApp()) {
    throw new Error("Приложение не запущено в Telegram WebApp");
  }

  const magicLinkUrl = import.meta.env.VITE_MAGIC_LINK_URL;
  if (!magicLinkUrl) {
    throw new Error("URL для magic link не настроен");
  }

  try {
    const response = await fetch(magicLinkUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        initData: window.Telegram.WebApp.initData,
        initDataUnsafe: window.Telegram.WebApp.initDataUnsafe,
      }),
    });

    if (!response.ok) {
      throw new Error(`Ошибка запроса: ${response.status}`);
    }

    const data = await response.json();
    return data.magic_link;
  } catch (error) {
    console.error("Ошибка получения magic link:", error);
    throw error;
  }
};

// Преобразование пользователя Telegram в пользователя приложения

// Инициализация Telegram WebApp
export const initTelegramWebApp = (): void => {
  if (!isTelegramApiAvailable()) {
    return;
  }

  try {
    window.Telegram.WebApp.ready();
  } catch (error) {
    console.warn("Не удалось сообщить Telegram о готовности приложения:", error);
  }

  if (isTelegramWebApp()) {
    console.log("Telegram WebApp initialized");
    console.log("initData:", window.Telegram.WebApp.initData);
    console.log("initDataUnsafe:", JSON.stringify(window.Telegram.WebApp.initDataUnsafe));

    // Устанавливаем цвета в соответствии с темой приложения
    // window.Telegram.WebApp.setHeaderColor('#ffffff');
    // window.Telegram.WebApp.setBackgroundColor('#ffffff');

    // Включаем подтверждение закрытия
    // window.Telegram.WebApp.enableClosingConfirmation();
  } else {
    console.log(
      "Обнаружен Telegram WebView без initData. Работаем в веб-режиме, но сообщаем Telegram о готовности для скрытия системного прелоадера"
    );
  }
};

// Открытие ссылки в Telegram
export const openTelegramLink = (url: string): void => {
  if (isTelegramWebApp()) {
    window.Telegram.WebApp.openTelegramLink(url);
  } else {
    window.open(url, "_blank");
  }
};

// Открытие обычной ссылки
export const openLink = (url: string): void => {
  if (isTelegramWebApp()) {
    window.Telegram.WebApp.openLink(url);
  } else {
    window.open(url, "_blank");
  }
};

// Закрытие Telegram Mini App
export const closeTelegramApp = (): void => {
  if (isTelegramWebApp()) {
    window.Telegram.WebApp.close();
  }
};

// Закрытие Telegram Mini App с очисткой данных
export const logoutAndCloseTelegramApp = async (): Promise<void> => {
  try {
    console.log("Начинаем очистку данных перед закрытием Telegram приложения");

    // Очищаем локальное хранилище
    localStorage.clear();
    console.log("localStorage очищен");

    // Очищаем sessionStorage
    sessionStorage.clear();
    console.log("sessionStorage очищен");

    // Очищаем cookies приложения
    document.cookie.split(";").forEach((c) => {
      const eqPos = c.indexOf("=");
      const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
      if (name) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      }
    });
    console.log("Cookies очищены");

    console.log("Все локальные данные очищены, закрываем Telegram приложение");

    // Закрываем Telegram приложение
    if (isTelegramWebApp()) {
      window.Telegram.WebApp.close();
    }
  } catch (error) {
    console.error("Ошибка при очистке данных:", error);
    // Все равно пытаемся закрыть приложение
    if (isTelegramWebApp()) {
      window.Telegram.WebApp.close();
    }
  }
};

// Показать уведомление
export const showAlert = (message: string, callback?: () => void): void => {
  if (isTelegramWebApp()) {
    window.Telegram.WebApp.showAlert(message, callback);
  } else {
    alert(message);
    if (callback) callback();
  }
};

// Показать подтверждение
export const showConfirm = (message: string, callback?: (confirmed: boolean) => void): void => {
  if (isTelegramWebApp()) {
    window.Telegram.WebApp.showConfirm(message, (confirmed: boolean) => {
      if (callback) callback(confirmed);
    });
  } else {
    const confirmed = confirm(message);
    if (callback) callback(confirmed);
  }
};

// ======================== THEME FUNCTIONS ========================

// Конвертация HEX в HSL для совместимости с shadcn/ui
const convertToHSL = (hex: string): string => {
  // Удаляем # если есть
  const cleanHex = hex.replace('#', '');
  
  // Конвертируем в RGB
  const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
  const b = parseInt(cleanHex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  // Возвращаем в формате "hue saturation lightness" (без %)
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};


// Получение параметров темы Telegram
export const getTelegramThemeParams = (): TelegramThemeParams | null => {
  if (isTelegramWebApp() && window.Telegram.WebApp.themeParams) {
    return window.Telegram.WebApp.themeParams;
  }
  
  return null;
};

// Получение цветовой схемы Telegram
export const getTelegramColorScheme = (): 'light' | 'dark' => {
  if (isTelegramWebApp()) {
    return window.Telegram.WebApp.colorScheme;
  }
  
  return 'light';
};

// Применение темы Telegram к CSS переменным
export const applyTelegramTheme = (theme: TelegramThemeParams): void => {
  const root = document.documentElement;
  
  console.group('🎨 Telegram Theme Applied');
  console.log('Theme Params:', theme);
  
  // Основные цвета фона и текста
  if (theme.bg_color) {
    root.style.setProperty('--background', convertToHSL(theme.bg_color));
    console.log('Background:', theme.bg_color, '→', convertToHSL(theme.bg_color));
  }
  if (theme.text_color) {
    root.style.setProperty('--foreground', convertToHSL(theme.text_color));
    console.log('Foreground:', theme.text_color, '→', convertToHSL(theme.text_color));
  }
  
  // Цвета кнопок и primary элементов
  if (theme.button_color) {
    root.style.setProperty('--primary', convertToHSL(theme.button_color));
    console.log('Primary:', theme.button_color, '→', convertToHSL(theme.button_color));
  }
  if (theme.button_text_color) {
    root.style.setProperty('--primary-foreground', convertToHSL(theme.button_text_color));
    console.log('Primary Foreground:', theme.button_text_color, '→', convertToHSL(theme.button_text_color));
  }
  
  // Вторичные цвета
  if (theme.secondary_bg_color) {
    root.style.setProperty('--secondary', convertToHSL(theme.secondary_bg_color));
    root.style.setProperty('--card', convertToHSL(theme.secondary_bg_color));
    root.style.setProperty('--popover', convertToHSL(theme.secondary_bg_color));
    root.style.setProperty('--muted', convertToHSL(theme.secondary_bg_color));
    console.log('Secondary:', theme.secondary_bg_color, '→', convertToHSL(theme.secondary_bg_color));
  }
  
  // Дополнительные цвета
  if (theme.hint_color) {
    root.style.setProperty('--muted-foreground', convertToHSL(theme.hint_color));
    console.log('Muted Foreground:', theme.hint_color, '→', convertToHSL(theme.hint_color));
  }
  if (theme.link_color) {
    root.style.setProperty('--accent', convertToHSL(theme.link_color));
    root.style.setProperty('--accent-foreground', convertToHSL(theme.text_color || '#000000'));
    console.log('Accent:', theme.link_color, '→', convertToHSL(theme.link_color));
  }
  
  // Обновляем foreground цвета для карточек и других элементов
  if (theme.text_color) {
    root.style.setProperty('--card-foreground', convertToHSL(theme.text_color));
    root.style.setProperty('--popover-foreground', convertToHSL(theme.text_color));
    root.style.setProperty('--secondary-foreground', convertToHSL(theme.text_color));
  }
  
  // Обновляем border цвета для лучшей адаптации
  if (theme.hint_color) {
    // Используем hint_color как основу для границ
    const borderHSL = convertToHSL(theme.hint_color);
    root.style.setProperty('--border', borderHSL);
    root.style.setProperty('--input', borderHSL);
    console.log('Border/Input:', theme.hint_color, '→', borderHSL);
  }
  
  console.log('CSS Variables Updated:', Object.keys(theme).length);
  console.groupEnd();
};
